import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Popup from "../../../components/Popup";
import UploadAudio from "../../../components/UploadAudio";
import UploadSubtitle from "../../../components/UploadSubtitle";
import {
  ActionsCell,
  ClickableRow,
  EmptyState,
  IconButton,
  Page,
  Pagination,
  SearchInput,
  StatusBadge,
  TableSkeleton,
  usePagination,
} from "../../../components/ui";
import { DetailHeader } from "../../../components/detail";
import { jwplayerRequest } from "../../../services/jwplayer";
import { formatDuration } from "../../../utils/format";
import { capitalize, matches, newestFirst } from "../../../utils/list";
import { canEditContent } from "../../../utils/roles";
import ContentInfoForm, {
  EMPTY_CONTENT,
  exclusivePrice,
  updatedReleaseYear,
} from "./ContentInfoForm";
import MediaUploadCard, { readVideoDuration } from "./MediaUploadCard";
import {
  jwMetadata,
  repairMediaDuration,
  uploadDirect,
  useDurationCheck,
  waitForJwDuration,
} from "./mediaUploads";
import "./content-form.css";

/** JW tags ending with this suffix are managed by the API and hidden. */
const EPISODE_TAG = "-episode";

/** Form values of an episode (`null`: new episode). */
export const episodeFormValues = (episode) =>
  episode
    ? {
        ...EMPTY_CONTENT,
        season: episode.season || "default",
        title: episode.title || "",
        description: episode.description || "",
        status: episode.status ?? true,
        language: episode.default_language || "",
        language_code:
          episode.language_code ||
          episode.translated_content?.[0]?.language_code ||
          "",
        release_date: episode.release_year
          ? new Date(episode.release_year).toISOString().split("T")[0]
          : "",
        seo_tags: episode.seo_tags || [],
        jw_tags: (episode.jw_tags || []).filter(
          (tag) => !tag.endsWith(EPISODE_TAG)
        ),
        content_type: episode.content_type || "default",
        price: episode.price ?? 0,
        monetization: episode.monetization ?? true,
        availability: episode.availability || "upcoming",
        bannerUrl: episode.thumbnail?.static_thumbnail_url || "",
      }
    : { ...EMPTY_CONTENT, season: "default", monetization: true };

/** Body of the episode create / update requests. */
export const episodePayload = (values, { seriesId, episode, pricing }) => ({
  tv_show_id: seriesId,
  release_year: updatedReleaseYear(values.release_date, episode?.release_year),
  category: episode?.category || "episode",
  season: values.season,
  title: values.title,
  imageBase64: values.imageBase64,
  isThumbnailSelected: Boolean(values.imageBase64),
  description: values.description,
  default_language: values.language,
  language_code: values.language_code,
  seo_tags: values.seo_tags,
  jw_tags: [
    ...values.jw_tags,
    ...(episode?.jw_tags || []).filter((tag) => tag.endsWith(EPISODE_TAG)),
  ],
  monetization: values.monetization,
  status: values.status,
  ...(pricing && {
    content_type: values.content_type,
    price: exclusivePrice(values),
  }),
  availability: values.availability,
});

/**
 * Validation of an episode: every field but the banner and the price, a
 * season, a price for exclusive content and a banner for an active episode.
 */
export const episodeError = (payload, hasBanner) => {
  const missing = Object.entries(payload).some(
    ([key, value]) =>
      key !== "imageBase64" &&
      key !== "price" &&
      (value === null ||
        value === undefined ||
        value === "" ||
        ((key === "season" || key === "content_type") && value === "default"))
  );
  if (missing) return "Please Fill Episode Info Completely!";
  if (payload.content_type === "exclusive" && !(payload.price > 0)) {
    return "Please enter a price for exclusive content!";
  }
  if (!hasBanner && payload.status && !payload.imageBase64) {
    return "Please upload banner image!";
  }
  return null;
};

/** Add / edit form of an episode, with its video, subtitles and audio tracks. */
const EpisodeEditor = ({ config, series, seriesId, seasons, episodeId, onSaved, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [episode, setEpisode] = useState(null);
  const [loading, setLoading] = useState(Boolean(episodeId));
  const [formKey, setFormKey] = useState(0);
  // Duration of the picked video file, and JW id of a video linked with it.
  const [duration, setDuration] = useState(null);
  const [unverifiedId, setUnverifiedId] = useState(null);

  useEffect(() => {
    if (!episodeId) return;
    config.loadEpisode(episodeId).then((data) => {
      setEpisode(data?._id ? data : null);
      setLoading(false);
    });
    // The editor is remounted for another episode.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [episodeId]);

  const linkMedia = async (body) => {
    const response = await config.linkMedia(episode._id, body);
    setEpisode((current) => ({
      ...current,
      ...body,
      ...response?.data?.updatedEpisode,
    }));
    onSaved();
  };

  const checkingDuration = useDurationCheck(
    episode?.media_id,
    episode?.duration,
    unverifiedId,
    async (jwDuration) => {
      await linkMedia({ media_id: episode.media_id, duration: jwDuration });
      setUnverifiedId(null);
    }
  );

  const jwTags = (data) => [...(data.jw_tags || []), ...config.extraJwTags(series)];

  const uploadVideo = async (file, report) => {
    const mediaId = await uploadDirect(
      file,
      jwMetadata(
        { ...episode, jw_tags: jwTags(episode) },
        config.tagType,
        series?.genre
      ),
      { mimeType: file.type || "video/mp4", jwTag: true }
    );
    if (!mediaId) throw new Error("Episode upload failed");
    report.onProcessing();
    // Short videos are processed within a minute; longer ones are linked with
    // the duration of the local file until JW knows the real one.
    const jwDuration = await waitForJwDuration(mediaId);
    await linkMedia({
      media_id: mediaId,
      duration: jwDuration || duration || undefined,
    });
    if (!jwDuration) setUnverifiedId(mediaId);
  };

  const payloadOf = (values) =>
    episodePayload(values, { seriesId, episode, pricing: config.pricing });

  const validate = (values) =>
    seasons.length === 0
      ? config.texts.noSeasons
      : episodeError(
          payloadOf(values),
          Boolean(episode?.thumbnail?.static_thumbnail_url)
        );

  const handleSubmit = async (values) => {
    const payload = payloadOf(values);
    if (!episode) {
      const created = await dispatch(config.create(payload.season, payload));
      if (!created?.savedEpisode) {
        toast.error(t("Error In General Content of Episode"));
        return;
      }
      const data = await config.loadEpisode(created.savedEpisode._id);
      setEpisode(data?._id ? data : created.savedEpisode);
      toast.success(t("Episode Info Submitted Successfully!"));
    } else {
      const response = await config.update(payload, episode._id);
      if (!response) {
        toast.error(t("Error In General Content of Episode"));
        return;
      }
      const data = await config.loadEpisode(episode._id);
      if (data?._id) {
        setEpisode(data);
        if (data.media_id) {
          // Keep the JW metadata in sync with the new info.
          jwplayerRequest({
            body: {
              metadata: jwMetadata(
                { ...data, jw_tags: jwTags(data) },
                config.tagType,
                series?.genre
              ),
            },
            url: `/media/${data.media_id}/`,
            method: "patch",
          }).catch(() => {});
        }
      }
      toast.success(t(" Episode Info Updated Successfully!"));
    }
    setFormKey((key) => key + 1);
    onSaved();
  };

  if (loading) {
    return (
      <section className="ui-card content-side-card">
        <TableSkeleton rows={4} />
      </section>
    );
  }

  const hasVideo = Boolean(episode?.media_id);
  return (
    <section className="episode-editor" aria-label={t(episode ? "Edit Episode" : "Add Episode")}>
      <div className="episode-editor-head">
        <h2 className="content-section-title">
          {episode ? `${t("Edit Episode")} · ${episode.title}` : t("Add Episode")}
        </h2>
        <IconButton icon="ri-close-line" label={t("Close")} onClick={onClose} />
      </div>

      {episode ? (
        <div className="content-media">
          <MediaUploadCard
            title={t("Video")}
            mediaId={episode.media_id}
            upload={uploadVideo}
            onFileSelected={(file) => readVideoDuration(file).then(setDuration)}
            note={
              checkingDuration
                ? t("Checking the video duration with JW Player…")
                : null
            }
            successKey="Video Uploaded Successfully!"
            failureKey="Video Uploading Failed!"
          />
          <div className="content-side-stack">
            <section className="ui-card content-side-card">
              <h3 className="content-side-title">{t("add subtitle files")}</h3>
              {!hasVideo && <p className="ui-hint">{t("Upload the video first")}</p>}
              <UploadSubtitle
                media_id={episode.media_id}
                mediaObjId={episode._id}
                disableProp={!hasVideo}
                generalContentObject={[episode]}
                addURL={"add-subtitles-for-episode"}
                deleteURL={"delete-subtitles-of-episode"}
                getURL={"get-subtitles-by-episode"}
              />
            </section>
            <section className="ui-card content-side-card">
              <h3 className="content-side-title">{t("add audio files")}</h3>
              {!hasVideo && <p className="ui-hint">{t("Upload the video first")}</p>}
              <UploadAudio
                media_id={episode.media_id}
                mediaObjId={episode._id}
                disableProp={!hasVideo}
                generalContentObject={[episode]}
                addURL={"add-audio-track-of-episode"}
                deleteURL={"delete-audio-track-of-episode"}
                getURL={"get-audio-tracks-by-episode"}
              />
            </section>
          </div>
        </div>
      ) : (
        <p className="ui-hint episode-editor-hint">
          <i className="ri-information-line" aria-hidden="true"></i>
          {t("Save the episode to upload its video")}
        </p>
      )}

      <ContentInfoForm
        key={`${episode?._id || "new"}-${formKey}`}
        initialValues={episodeFormValues(episode)}
        pricing={config.pricing}
        validate={validate}
        onSubmit={handleSubmit}
        submitLabel={episode ? t("Update") : t("submit")}
        onCancel={onClose}
        canRelease={hasVideo}
        leadFields={(values, set) => (
          <div className="ui-field">
            <label htmlFor="episode-season">{t("Season")}</label>
            <select
              id="episode-season"
              className="ui-input"
              value={values.season}
              onChange={(e) => set("season")(e.target.value)}
            >
              <option disabled value="default">
                {t("Choose Season")}
              </option>
              {seasons.map((season, index) => (
                <option key={season._id} value={season._id}>
                  {t("Season")} {index + 1}
                </option>
              ))}
            </select>
          </div>
        )}
      />
    </section>
  );
};

/**
 * Episodes of a TV show / podcast: list, filters, and the add / edit form.
 *
 * - `idParam`: route param holding the series id
 * - `loadSeries`, `loadSeasons`, `loadEpisodes`, `loadEpisode`: fetches
 * - `create(seasonId, body)`: redux thunk creating an episode
 * - `update(body, id)`, `remove(id, seasonId)`, `linkMedia(id, body)`: requests
 * - `tagType`: JW genre tag type; `extraJwTags(series)`: JW tags added on upload
 * - `pricing`: episodes have a content type and price
 * - `seriesPath(id)`, `infoPath(episodeId)`: related pages
 * - `texts`: `{ noSeasons, editSeries }` translation keys
 * - `section`: permission section ("series" by default, "podcasts")
 */
const EpisodesPage = (config) => {
  const { t } = useTranslation();
  const history = useHistory();
  const canEdit = canEditContent(config.section || "series");
  const seriesId = useParams()[config.idParam];

  const [series, setSeries] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  // Editor: null (closed), { id: null } (new episode) or { id }.
  const [editor, setEditor] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [season, setSeason] = useState("all");
  const [search, setSearch] = useState("");

  const setEpisode = (id, changes) =>
    setEpisodes((current) =>
      current.map((item) => (item._id === id ? { ...item, ...changes } : item))
    );

  const refresh = () =>
    config.loadEpisodes(seriesId).then((result) => {
      const list = newestFirst(result?.episodes) || [];
      setEpisodes(list);
      return list;
    });

  useEffect(() => {
    config.loadSeries(seriesId).then((data) => setSeries(data?._id ? data : null));
    config
      .loadSeasons(seriesId)
      .then((data) => setSeasons(data?.seasons || []));
    refresh().then((list) => {
      setLoading(false);
      if (list.length === 0 && canEdit) setEditor({ id: null });
      // Episodes saved before JW Player computed their duration get it now.
      list.forEach((episode) =>
        repairMediaDuration(episode, (duration) =>
          config.linkMedia(episode._id, { media_id: episode.media_id, duration })
        )
          .then((response) => {
            const updated = response?.data?.updatedEpisode;
            if (updated?.duration) setEpisode(episode._id, { duration: updated.duration });
          })
          .catch((error) => console.error("Error while repairing duration:", error))
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesId]);

  const seasonNumber = (seasonId) =>
    seasons.findIndex((item) => item._id === seasonId) + 1;

  const filtered = useMemo(
    () =>
      episodes.filter(
        (episode) =>
          (season === "all" || episode.season === season) &&
          matches(episode.title, search)
      ),
    [episodes, season, search]
  );
  const pagination = usePagination(filtered, 10, [season, search]);

  const openEditor = (id) => {
    setEditor({ id, key: Date.now() });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (episode) => {
    try {
      await config.remove(episode._id, episode.season);
      toast.success(t("Episode Deleted Successfully!"));
      if (editor?.id === episode._id) setEditor(null);
      refresh();
    } catch (error) {
      toast.error(t("Error In Deleting Episode"));
    }
  };

  return (
    <Page>
      <DetailHeader
        title={series?.title ? `${t("Episodes")} · ${series.title}` : t("Episodes")}
        actions={
          canEdit && (
            <>
              <Link
                to={config.seriesPath(seriesId)}
                className="ui-btn ui-btn-secondary"
              >
                <i className="ri-pencil-line" aria-hidden="true"></i>
                {t(config.texts.editSeries)}
              </Link>
              <button type="button" className="ui-btn" onClick={() => openEditor(null)}>
                <i className="ri-add-line" aria-hidden="true"></i>
                {t("Add Episode")}
              </button>
            </>
          )
        }
      />

      {editor && (
        <EpisodeEditor
          key={editor.key || editor.id || "new"}
          config={config}
          series={series}
          seriesId={seriesId}
          seasons={seasons}
          episodeId={editor.id}
          onSaved={refresh}
          onClose={() => setEditor(null)}
        />
      )}

      <section className="ui-card">
        <div className="ui-toolbar">
          <select
            className="ui-input episode-season-filter"
            aria-label={t("Choose Season")}
            value={season}
            onChange={(e) => setSeason(e.target.value)}
          >
            <option value="all">{t("All seasons")}</option>
            {seasons.map((item, index) => (
              <option key={item._id} value={item._id}>
                {t("Season")} {index + 1}
              </option>
            ))}
          </select>
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={t("Search episodes")}
          />
        </div>

        {loading ? (
          <TableSkeleton />
        ) : filtered.length === 0 ? (
          <EmptyState icon="ri-play-list-line">{t("No Data Found")}</EmptyState>
        ) : (
          <>
            <div className="ui-table-wrap">
              <table className="ui-table">
                <thead>
                  <tr>
                    <th>{t("Episode")}</th>
                    <th>{t("Season")}</th>
                    <th>{t("Release Date")}</th>
                    <th>{t("status")}</th>
                    <th className="ui-cell-actions">
                      <span className="sr-only">{t("action")}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pagination.pageItems.map((episode) => (
                    <ClickableRow
                      key={episode._id}
                      to={config.infoPath(episode._id)}
                      selected={editor?.id === episode._id}
                    >
                      <td>
                        <div className="ui-media">
                          <img
                            className="ui-media-thumb"
                            src={episode.thumbnail?.static_thumbnail_url || ""}
                            alt=""
                            loading="lazy"
                          />
                          <div style={{ minWidth: 0 }}>
                            <Link
                              to={config.infoPath(episode._id)}
                              className="ui-media-title"
                              title={episode.title}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {episode.title}
                            </Link>
                            <span className="ui-media-meta">
                              {[
                                episode.media_id
                                  ? formatDuration(episode.duration)
                                  : t("No video"),
                                capitalize(episode.default_language),
                                episode.monetization
                                  ? t("Monetized")
                                  : t("Not monetized"),
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="ui-cell-muted">
                        {seasonNumber(episode.season)
                          ? `${t("Season")} ${seasonNumber(episode.season)}`
                          : "N/A"}
                      </td>
                      <td className="ui-cell-muted">
                        {episode.release_year?.substring(0, 10) || "N/A"}
                      </td>
                      <td>
                        <StatusBadge active={episode.status === true} />
                      </td>
                      <ActionsCell>
                        <IconButton
                          icon="ri-eye-line"
                          label={t("View")}
                          onClick={() => history.push(config.infoPath(episode._id))}
                        />
                        {canEdit && (
                          <>
                            <IconButton
                              icon="ri-pencil-line"
                              label={t("Edit")}
                              onClick={() => openEditor(episode._id)}
                            />
                            <IconButton
                              icon="ri-delete-bin-line"
                              label={t("Delete")}
                              tone="danger"
                              onClick={() => setToDelete(episode)}
                            />
                          </>
                        )}
                      </ActionsCell>
                    </ClickableRow>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination {...pagination} onChange={pagination.setPage} />
          </>
        )}
      </section>

      {toDelete && (
        <Popup
          open={true}
          setOpen={() => setToDelete(null)}
          deleteHandle={() => handleDelete(toDelete)}
        >
          <div className="model-wrapper">
            {t("Are you sure, you want to delete")} {toDelete.title}?
          </div>
        </Popup>
      )}
    </Page>
  );
};

export default EpisodesPage;
